import type { OperationError, OperationResult, LibrarySearchEntity } from 'venn-api';
import { getFund, getPrivateFund, getPrivatePortfolio, getSpecificPortfolioV3 } from 'venn-api';
import { atomFamily, selectorFamily, waitForAll } from 'recoil';
import { AnalysisSubject, type AnalysisSubjectType, assertNotNil, StudioErrorsEnum } from 'venn-utils';
import {
  incrementOnStudioReset,
  incrementOnSubjectChange,
  incrementOnWorkspaceConfigurationChange,
} from '../../effects/signalEffects';
import type { Subject, SubjectWithOptionalFeeAndFilters } from '../types';
import { toSubjectOnly } from '../types';
import { allocatorAnalysisSubject } from './allocator';
import { proxySetIdState } from './view';

/**
 * An id that is a dependency of apiAnalysisSubjectQuery
 * so that its cache will be cleared when this atom changes
 * See https://recoiljs.org/docs/guides/asynchronous-data-queries/#use-a-request-id for more info
 */
const apiAnalysisSubjectQueryId = atomFamily<number, Subject>({
  key: 'apiAnalysisSubjectQueryId',
  default: 0,
  effects: (subject) => [
    incrementOnStudioReset,
    incrementOnSubjectChange(subject),
    incrementOnWorkspaceConfigurationChange,
  ],
});

const apiAnalysisSubjectQuery = selectorFamily<AnalysisSubject | undefined, Subject>({
  key: 'apiAnalysisSubjectQuery',
  get:
    (subject) =>
    async ({ get }) => {
      try {
        get(apiAnalysisSubjectQueryId(subject));

        const proxySetId = get(proxySetIdState);

        const portfolio = subject.portfolioId
          ? (await getSpecificPortfolioV3(subject.portfolioId, subject.portfolioVersion, proxySetId)).content
          : undefined;
        const fund = subject.fundId ? (await getFund(subject.fundId, proxySetId)).content : undefined;
        const privatePortfolio = subject.privatePortfolioId
          ? (await getPrivatePortfolio(subject.privatePortfolioId)).content
          : undefined;
        const privateFund = subject.privateFundId ? (await getPrivateFund(subject.privateFundId)).content : undefined;
        const item = portfolio ?? fund ?? privatePortfolio ?? privateFund;
        if (!item) {
          return undefined;
        }

        return new AnalysisSubject(item, getLibrarySearchEntityAnalysisSubjectType(subject), proxySetId);
      } catch (error) {
        const { status } = error as OperationResult<OperationError>;
        if (status === 403) {
          throw new Error(StudioErrorsEnum.SUBJECT_NO_PERMISSION);
        } else {
          throw new Error(StudioErrorsEnum.SUBJECT_FAILED_TO_LOAD);
        }
      }
    },
});

/** Subject query excluding any impact of allocator panel. */
export const originalAnalysisSubjectQuery = selectorFamily<AnalysisSubject | undefined, Subject | undefined>({
  key: 'originalAnalysisSubjectQuery',
  get:
    (subject) =>
    ({ get }) => {
      const subjectOnly = toSubjectOnly(subject);
      if (!subjectOnly) {
        return undefined;
      }
      return get(apiAnalysisSubjectQuery(subjectOnly));
    },
});

/** Single subject query, including any impact from allocator panel. */
export const analysisSubjectQuery = selectorFamily<AnalysisSubject, SubjectWithOptionalFeeAndFilters>({
  key: 'analysisSubject',
  get:
    (subject) =>
    ({ get }) => {
      const subjectToQuery: Subject | undefined = toSubjectOnly(subject);
      return assertNotNil(
        get(allocatorAnalysisSubject(subjectToQuery)) ?? get(originalAnalysisSubjectQuery(subjectToQuery)),
      );
    },
});

/** Uses {@link analysisSubjectQuery} to query for multiple subjects. */
export const analysisSubjects = selectorFamily<AnalysisSubject[], Subject[]>({
  key: 'analysisSubjects',
  get: (subjects) => () => waitForAll(subjects.map((subject) => analysisSubjectQuery(subject))),
});

export const getLibrarySearchEntityAnalysisSubjectType = (
  entity: LibrarySearchEntity | Subject,
): AnalysisSubjectType => {
  return entity.privateFundId
    ? 'private-investment'
    : entity.privatePortfolioId
      ? 'private-portfolio'
      : entity.fundId
        ? 'investment'
        : 'portfolio';
};
